/**
 * Product Card Media Slider
 * Initializes Swiper sliders for product cards when the collection setting is enabled
 */

// Prevent multiple declarations
if (typeof window.ProductCardSlider === "undefined") {
  window.ProductCardSlider = class ProductCardSlider {
    constructor() {
      this.sliders = [];
      this.init();
    }

    init() {
      // Wait for Swiper to be available
      if (
        typeof window.MinimogLibs === "undefined" ||
        !window.MinimogLibs.Swiper
      ) {
        setTimeout(() => this.init(), 100);
        return;
      }

      this.initSliders();
      this.setupEventListeners();
    }

    initSliders() {
      const sliderElements = document.querySelectorAll("[data-product-slider]");

      sliderElements.forEach((element) => {
        const swiper = this.createSlider(element);
        if (swiper) {
          this.sliders.push(swiper);
        }
      });
    }

    createSlider(element) {
      const container = element.querySelector(".swiper-container");
      if (!container) return null;

      const slides = container.querySelectorAll(".swiper-slide");
      if (slides.length <= 1) return null;

      const prevButton = element.querySelector(".m-product-card__slider-prev");
      const nextButton = element.querySelector(".m-product-card__slider-next");

      const config = {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: slides.length > 2, // Re-enable loop for smooth infinite scrolling
        initialSlide: 0, // Always start with the first slide
        speed: 300,
        grabCursor: true,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        autoHeight: false, // Disable auto height to prevent sizing issues
        resizeObserver: true,
        preventInteractionOnTransition: false,
        allowTouchMove: true,
        freeMode: false,
        centeredSlides: false,
        lazy: {
          loadPrevNext: true,
          loadPrevNextAmount: 2,
        },
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton,
        },
        breakpoints: {
          // Ensure consistent behavior across breakpoints
          320: {
            slidesPerView: 1,
            spaceBetween: 0,
          },
          768: {
            slidesPerView: 1,
            spaceBetween: 0,
          },
          1024: {
            slidesPerView: 1,
            spaceBetween: 0,
          },
        },
        on: {
          init: function () {
            // Force container constraints
            const container = this.el;
            const wrapper = container.querySelector(".swiper-wrapper");
            const slides = container.querySelectorAll(".swiper-slide");

            if (wrapper) {
              wrapper.style.width = "100%";
              wrapper.style.maxWidth = "100%";
            }

            slides.forEach((slide) => {
              slide.style.width = "100%";
              slide.style.maxWidth = "100%";
              slide.style.flexShrink = "0";
            });

            // Update swiper after constraints
            setTimeout(() => {
              this.update();
              this.updateSize();
              this.updateSlides();
              this.updateProgress();
              this.updateSlidesClasses();
            }, 0);

            // Initialize lazy loading for first slide using IntersectionObserver
            const swiperInstance = this;
            const sliderElement = element;
            if (window.ProductCardSlider) {
              window.ProductCardSlider.prototype.initLazyLoadingForFirstSlide.call(
                {},
                swiperInstance,
                sliderElement
              );
            }
          },
          resize: function () {
            // Reapply constraints on resize
            const container = this.el;
            const wrapper = container.querySelector(".swiper-wrapper");
            const slides = container.querySelectorAll(".swiper-slide");

            if (wrapper) {
              wrapper.style.width = "100%";
              wrapper.style.maxWidth = "100%";
            }

            slides.forEach((slide) => {
              slide.style.width = "100%";
              slide.style.maxWidth = "100%";
            });
          },
          slideChange: function () {
            // Pause any media when sliding
            window.pauseAllMedia?.(element);
          },
        },
      };

      try {
        return new window.MinimogLibs.Swiper(container, config);
      } catch (error) {
        console.warn("Failed to initialize product card slider:", error);
        return null;
      }
    }

    setupEventListeners() {
      // Re-initialize sliders when collection content is updated (e.g., via AJAX)
      if (window.MinimogEvents) {
        window.MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => {
          console.log("updated");
          this.destroy();
          setTimeout(() => this.initSliders(), 100);
        });
      }

      // Handle Shopify section loading/unloading in theme editor
      if (window.Shopify?.designMode) {
        document.addEventListener("shopify:section:load", () => {
          setTimeout(() => this.initSliders(), 100);
        });

        document.addEventListener("shopify:section:unload", () => {
          this.destroy();
        });
      }

      // --- Mobile tap/swipe logic for product card slider images ---
      // Only run on touch devices
      if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
        document.addEventListener("DOMContentLoaded", () => {
          this.setupMobileTapToProduct();
        });
      }
    }

    setupMobileTapToProduct() {
      // For each product card slider, add tap/swipe logic to .m-product-card__link
      document
        .querySelectorAll("[data-product-slider] .m-product-card__link")
        .forEach((link) => {
          let touchStartX = 0,
            touchStartY = 0,
            touchStartTime = 0;
          let moved = false;

          link.addEventListener(
            "touchstart",
            function (e) {
              if (e.touches.length === 1) {
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
                touchStartTime = Date.now();
                moved = false;
              }
            },
            { passive: true }
          );

          link.addEventListener(
            "touchmove",
            function (e) {
              if (e.touches.length === 1) {
                const dx = Math.abs(e.touches[0].clientX - touchStartX);
                const dy = Math.abs(e.touches[0].clientY - touchStartY);
                if (dx > 10 || dy > 10) moved = true;
              }
            },
            { passive: true }
          );

          link.addEventListener(
            "touchend",
            function (e) {
              const touchEndX = e.changedTouches[0].clientX;
              const touchEndY = e.changedTouches[0].clientY;
              const dx = Math.abs(touchEndX - touchStartX);
              const dy = Math.abs(touchEndY - touchStartY);
              const dt = Date.now() - touchStartTime;

              // Thresholds: adjust as needed
              const TAP_MAX_DIST = 10; // px
              const TAP_MAX_TIME = 300; // ms

              if (
                !moved &&
                dx < TAP_MAX_DIST &&
                dy < TAP_MAX_DIST &&
                dt < TAP_MAX_TIME
              ) {
                // Considered a tap, follow the link
                window.location = link.href;
              }
              // else: let the slider handle the swipe
            },
            { passive: true }
          );

          // Prevent default click on mobile to avoid double navigation
          link.addEventListener("click", function (e) {
            if ("ontouchstart" in window || navigator.maxTouchPoints > 0)
              e.preventDefault();
          });
        });
    }

    initLazyLoadingForFirstSlide(swiperInstance, sliderElement) {
      const productCard = sliderElement?.closest(".m-product-card");
      if (!productCard) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && swiperInstance?.slides) {
              const firstSlide = swiperInstance.slides[0];
              if (firstSlide) {
                const img = firstSlide.querySelector('img[loading="lazy"]');
                if (img) {
                  img.removeAttribute("loading");
                  // Force image to start loading immediately
                  if (img.src) {
                    const tempImg = new Image();
                    tempImg.src = img.src;
                  }
                  observer.unobserve(entry.target);
                }
              }
            }
          });
        },
        {
          rootMargin: "100px", // Start loading 100px before entering viewport
          threshold: 0.1, // Trigger when 10% of the product card is visible
        }
      );

      observer.observe(productCard);

      // Store observer for cleanup
      if (!sliderElement.lazyLoadObservers) {
        sliderElement.lazyLoadObservers = new Set();
      }
      sliderElement.lazyLoadObservers.add(observer);
    }

    destroy() {
      this.sliders.forEach((slider) => {
        if (slider && typeof slider.destroy === "function") {
          // Clean up IntersectionObservers
          const sliderElement = slider.el?.closest("[data-product-slider]");
          if (sliderElement && sliderElement.lazyLoadObservers) {
            sliderElement.lazyLoadObservers.forEach((observer) => {
              observer.disconnect();
            });
            sliderElement.lazyLoadObservers.clear();
            delete sliderElement.lazyLoadObservers;
          }
          slider.destroy(true, true);
        }
      });
      this.sliders = [];
    }
  }; // Close the class definition
} // Close the if statement

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.MinimogTheme = window.MinimogTheme || {};
    if (!window.MinimogTheme.ProductCardSlider) {
      window.MinimogTheme.ProductCardSlider = new window.ProductCardSlider();
    }
  });
} else {
  window.MinimogTheme = window.MinimogTheme || {};
  if (!window.MinimogTheme.ProductCardSlider) {
    window.MinimogTheme.ProductCardSlider = new window.ProductCardSlider();
  }
}
